import React from "react";
import { Box, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { CardGrid, Cards } from "@/types/dto/homepage/homepage-types";
import { SectionHeader } from "@/components/common/section-header";
import { sanitizeBasic } from "@/utils/sanitize-html";

interface PrivateTourPricingProps {
  tourData: CardGrid;
  moduleRankingPosition?: number | string;
}

const PrivateTourPricing = ({ tourData, moduleRankingPosition }: PrivateTourPricingProps) => {
  const { basicInfo, cards } = tourData || {};
  return (
    <Stack
      component="section"
      data-module-name="Private Touring Pricing"
      data-module-ranking={moduleRankingPosition}
      data-track="visibility"
      data-parent-container-type="private-touring-section"
      className="fluid-box"
      sx={{ bgcolor: "#F3FAFC" }}
    >
      <Stack
        className="full-width-container"
        spacing="4rem"
        sx={{
          p: { xs: "4rem 2.4rem", sm: "4rem 3.2rem", md: "6rem 10rem" },
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <SectionHeader
          headerText={basicInfo?.headerText}
          headerTestId="tour-pricing-header"
          copySx={{
            textAlign: "center",
            "& p": { margin: "0 !important" },
            maxWidth: "102rem",
            margin: "0 auto"
          }}
          headerSx={{ textAlign: "center" }}
          copyText={basicInfo?.copyText}
          copyTestId="tour-pricing-copy"
        />
        <Grid container spacing="2.4rem" data-testid="pricing-cards-container">
          {cards?.map((card: Cards, index: number) => (
            <Grid
              size={{ xs: 12, md: 4 }}
              key={card?.id || index}
              data-testid={`pricing-card-${index}`}
            >
              <Card
                component="div"
                sx={{
                  borderRadius: "2rem",
                  boxShadow: "0px 3px 6px 0px rgba(0, 0, 0, 0.10)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  textDecoration: "none",
                  overflow: "hidden",
                  backgroundColor: "white",
                  alignItems: "stretch"
                }}
              >
                {card?.image?.imageUrl && (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: 282
                    }}
                  >
                    <Image
                      src={card.image.imageUrl || ""}
                      alt={card.image.altText || "Tour image"}
                      fill
                      style={{ objectFit: "cover" }}
                      data-testid={`pricing-card-image-${index}`}
                    />
                  </Box>
                )}

                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e3e3e3",
                    padding: "2.4rem 1.6rem",
                    borderRadius: "0 0 2rem 2rem",
                    gap: "1.6rem"
                  }}
                >
                  <Typography
                    data-testid={`pricing-card-header-${index}`}
                    variant="h5"
                    component="h3"
                    sx={{
                      color: "primary.main",
                      lineHeight: "normal",
                      fontSize: "2.8rem"
                    }}
                  >
                    {card?.info?.headerText}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "27.7rem",
                      bgcolor: "warning.800",
                      borderRadius: "2rem",
                      height: "6px"
                    }}
                  />
                  {card?.info?.copyText && (
                    <Box
                      sx={{
                        fontSize: "1.6rem",
                        color: "black",
                        "& p": {
                          margin: "0 !important",
                          whiteSpace: "pre-line",
                          maxWidth: "102rem"
                        }
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: sanitizeBasic(card?.info?.copyText) }}
                      data-testid={`pricing-card-copy-${index}`}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Stack>
  );
};

export default PrivateTourPricing;
